import type { ControllerRenderProps, FieldPath, FieldValues } from "react-hook-form";

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useTranslationUtils } from "@/hooks/use-translation-utils";

import { ComboboxContent, ComboboxInput, ComboboxItem, ComboboxList, ComboboxProvider, ComboboxRoot } from "./ui/combobox";

export function CountryCombobox<T extends FieldValues, K extends FieldPath<T>>({ field }: { field: ControllerRenderProps<T, K> }) {
  const { getCountryName, getFilteredCountriesList } = useTranslationUtils();
  const [search, setSearch] = useState(field.value ? getCountryName(field.value) : "");
  const [open, setOpen] = useState(false);
  const comboboxRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation(["common"]);

  return (
    <ComboboxRoot open={open} onOpenChange={setOpen} modal>
      <ComboboxProvider open={open} setOpen={setOpen}>
        <ComboboxInput
          placeholder={t("common:search-country")}
          ref={comboboxRef}
          value={search}
          onChange={(e) => {
            const value = e.target.value;
            setSearch(value);
          }}
          onBlur={() => {
            if (open) {
              return;
            }
            setSearch(getCountryName(field.value));
          }}
        />
        <ComboboxContent comboboxRef={comboboxRef} listboxRef={listRef}>
          <ComboboxList ref={listRef}>
            {getFilteredCountriesList(search).length
              ? getFilteredCountriesList(search).map(({ code, name }) => (
                  <ComboboxItem
                    key={code}
                    value={code}
                    onClick={() => {
                      field.onChange(code);
                      setSearch(name);
                    }}
                  >
                    {name}
                  </ComboboxItem>
                ))
              : (
                  <span className="p-2 text-center text-neutral-400">
                    {t("common:no-country-found")}
                  </span>
                )}
          </ComboboxList>
        </ComboboxContent>
      </ComboboxProvider>
    </ComboboxRoot>
  );
}
